/**
 * 获取图片缩放比例
 * @param {Object} params - 参数对象
 * @param {Object} params.ImageBox - 容器尺寸 { w, h }
 * @param {Object} params.Image - 图片尺寸 { w, h }
 * @returns {Object} 缩放比例和计算后的尺寸
 */
import { DEFAULT_MESSAGES } from "../const/constants.js";

export const getScaleRatio = ({ ImageBox, Image }) => {
  const wRatio = ImageBox?.w / Image.w;
  const hRatio = ImageBox?.h / Image.h;
  const ratio = hRatio < wRatio ? hRatio : wRatio;
  return {
    Ratio: ratio,
    ImageWidth: Image.w * ratio,
    ImageHeight: Image.h * ratio,
  };
};

/**
 * Strip EXIF from JPEG: remove APP1 (0xFFE1) segment.
 */
function stripJpegExif(arrayBuffer) {
  const dataView = new DataView(arrayBuffer);
  const segments = [];
  segments.push(new Uint8Array(arrayBuffer, 0, 2)); // SOI marker

  let offset = 2;
  while (offset < dataView.byteLength - 1) {
    if (dataView.getUint8(offset) !== 0xff) break;

    const marker = dataView.getUint16(offset);

    // SOS marker (0xFFDA): image data starts here, append rest
    if (marker === 0xffda) {
      segments.push(new Uint8Array(arrayBuffer, offset));
      break;
    }

    const segmentLength = dataView.getUint16(offset + 2) + 2;

    // Skip APP1 (0xFFE1) — this is the EXIF segment
    if (marker !== 0xffe1) {
      segments.push(new Uint8Array(arrayBuffer, offset, segmentLength));
    }

    offset += segmentLength;
  }

  return new Blob(segments, { type: "image/jpeg" });
}

/**
 * Strip EXIF from PNG: remove eXIf chunk.
 * PNG structure: 8-byte signature + chunks (each: 4-byte length + 4-byte type + data + 4-byte CRC)
 */
function stripPngExif(arrayBuffer) {
  const dataView = new DataView(arrayBuffer);
  const bytes = new Uint8Array(arrayBuffer);
  const chunks = [];

  // PNG signature: 8 bytes
  chunks.push(bytes.slice(0, 8));

  let offset = 8;
  let hasExif = false;

  while (offset < dataView.byteLength) {
    const chunkDataLength = dataView.getUint32(offset);
    // chunk type: 4 bytes at offset+4
    const typeBytes = bytes.slice(offset + 4, offset + 8);
    const chunkType = String.fromCharCode(...typeBytes);
    // total chunk size: 4(length) + 4(type) + data + 4(CRC)
    const totalChunkSize = 12 + chunkDataLength;

    if (chunkType === "eXIf") {
      hasExif = true;
    } else {
      chunks.push(bytes.slice(offset, offset + totalChunkSize));
    }

    offset += totalChunkSize;
  }

  if (!hasExif) return null;
  return new Blob(chunks, { type: "image/png" });
}

/**
 * Strip EXIF data from a JPEG/PNG image URL to prevent browser auto-rotation.
 * - JPEG: removes APP1 (0xFFE1) segment
 * - PNG: removes eXIf chunk (PNG 1.6+)
 * @param {string} src - Image URL or base64 data URL
 * @returns {Promise<string>} URL without EXIF orientation data
 */
export async function stripExifFromUrl(src) {
  if (!src || src.startsWith("data:")) return src;

  try {
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    const dataView = new DataView(arrayBuffer);

    let blob = null;

    // JPEG: SOI marker 0xFFD8
    if (dataView.getUint16(0) === 0xffd8) {
      blob = stripJpegExif(arrayBuffer);
    }
    // PNG: signature 0x89504E47
    else if (dataView.getUint32(0) === 0x89504e47) {
      blob = stripPngExif(arrayBuffer);
    }

    if (!blob) return src;
    return URL.createObjectURL(blob);
  } catch (e) {
    console.warn("Strip EXIF failed, fallback to original src:", e);
    return src;
  }
}

/**
 * Get the natural dimensions of an image.
 * @param {string} url - Image URL
 * @param {boolean} isPdf - unused
 * @param {boolean} stripExif - whether to strip EXIF before reading dimensions
 * @returns {Promise<Object>} { width, height }
 */
export function getImgNatural(url, isPdf, stripExif = false) {
  return new Promise(async (resolve, reject) => {
    if (!url) {
      resolve({ width: 0, height: 0 });
      return;
    }

    // Only strip EXIF when explicitly enabled, so naturalWidth/Height match the rendered image
    const strippedUrl = stripExif ? await stripExifFromUrl(url) : url;
    let objectUrl = strippedUrl !== url ? strippedUrl : null;

    const temImg = new Image();
    temImg.src = strippedUrl;

    temImg.onload = function () {
      resolve({
        width: temImg.width,
        height: temImg.height,
      });
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    temImg.onerror = function (err) {
      console.log("任务图片识别失败", err);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(DEFAULT_MESSAGES.TASK_IMAGE_RECOGNITION_FAILED);
    };
  });
}
